import { Timestamp } from '@angular/fire/firestore';

/** Firestore stock field for last stock take date (replaces legacy field 47). */
export const STOCK_FIELD_STOCK_TAKE_DATE = '_stock_take_date';

/** Precomputed stock value: latest cost × on hand. */
export const STOCK_FIELD_STOCK_VALUE = '_stock_value';

/** Maximum top-value items shown on the stock take dashboard. */
export const STOCK_TAKE_DASHBOARD_TOP_ITEMS_LIMIT = 100;

/** Coverage KPI: store-wide value counted within this many days. */
export const STOCK_TAKE_DASHBOARD_COVERAGE_DAYS = 30;

/** Low stock-value KPI: items with `_stock_value` of this negative amount (ZAR) or more negative. */
export const STOCK_TAKE_DASHBOARD_LOW_STOCK_VALUE_ZAR = 2_000;

/** Never-counted KPI: stock value above this (ZAR) highlights the card red. */
export const STOCK_TAKE_DASHBOARD_NEVER_COUNTED_VALUE_DANGER_ZAR = 10_000;

/** High-risk KPI: top-100 value at risk above this share of total stock value highlights red. */
export const STOCK_TAKE_DASHBOARD_HIGH_RISK_VALUE_PERCENT_DANGER = 20;

/** Coverage KPI: below this percent is danger; between this and warning max is warning. */
export const STOCK_TAKE_DASHBOARD_COVERAGE_DANGER_PERCENT = 20;

/** Coverage KPI: at or below this percent (and above danger) is warning. */
export const STOCK_TAKE_DASHBOARD_COVERAGE_WARNING_PERCENT = 30;

/** Treat stock take dates older than this many years as never counted on the dashboard. */
export const STOCK_TAKE_DASHBOARD_NEVER_COUNTED_YEARS = 10;

/** Open stock takes older than this many days appear on the dashboard stale-open KPI. */
export const STOCK_TAKE_DASHBOARD_STALE_OPEN_DAYS = 3;

/** Prepared stock takes older than this many days are called out on the Open Stock Takes KPI. */
export const STOCK_TAKE_DASHBOARD_STALE_PREPARED_DAYS = 1;

/** Default name for a dashboard-created stock take of never-counted items. */
export const STOCK_TAKE_NEVER_COUNTED_STOCK_TAKE_NAME = 'Items Never Counted';

/** Default name for a dashboard-created stock take of high-value items not counted recently. */
export const STOCK_TAKE_HIGH_RISK_STOCK_TAKE_NAME = 'High Risk Items to Count';

/**
 * True when a top-value stock item is high-risk on the dashboard:
 * never counted, or last counted more than coverageDays ago.
 */
export function isStockTakeHighRiskTopValueItem(
  daysSinceCounted: number | null,
  coverageDays: number = STOCK_TAKE_DASHBOARD_COVERAGE_DAYS,
): boolean {
  return daysSinceCounted === null || daysSinceCounted > coverageDays;
}

/**
 * Filter top-value items to those not counted within the coverage window (dashboard high-risk KPI).
 */
export function filterStockTakeHighRiskTopValueItems<T extends { daysSinceCounted: number | null }>(
  items: T[],
  coverageDays: number = STOCK_TAKE_DASHBOARD_COVERAGE_DAYS,
): T[] {
  return items.filter((item) => isStockTakeHighRiskTopValueItem(item.daysSinceCounted, coverageDays));
}

/** Open-order list: items not counted within this many days. */
export const STOCK_TAKE_UNCOUNTED_ORDER_DAYS = 90;

/**
 * True when the last stock take date is missing or older than the given number of days.
 */
export function isStockTakeDateMissingOrOlderThan(
  raw: unknown,
  olderThanDays: number = STOCK_TAKE_UNCOUNTED_ORDER_DAYS,
  nowMs: number = Date.now(),
): boolean {
  const daysSinceCounted = resolveStockTakeDaysSinceCounted(raw, nowMs);
  return daysSinceCounted === null || daysSinceCounted > olderThanDays;
}

/** Uncounted age bands shown on the stock take dashboard (item counts only). */
export const STOCK_TAKE_DASHBOARD_UNCOUNTED_BAND_DEFS: readonly {
  days: number;
  label: string;
  tone: 'amber' | 'orange' | 'red' | 'red-dark';
}[] = [
  { label: '> 30 days', days: 30, tone: 'amber' },
  { label: '> 60 days', days: 60, tone: 'amber' },
  { label: '> 90 days', days: 90, tone: 'orange' },
  { label: '> 120 days', days: 120, tone: 'red' },
  { label: '> 150 days', days: 150, tone: 'red' },
  { label: '> 180 days', days: 180, tone: 'red-dark' },
];

/** Completed stock takes shown in dashboard history. */
export const STOCK_TAKE_DASHBOARD_HISTORY_LIMIT = 10;

/** Dates before this are treated as never counted (legacy / invalid placeholders). */
export const STOCK_TAKE_NEVER_COUNTED_CUTOFF_MS = Date.UTC(2000, 0, 1);

/**
 * Earliest valid last stock take date for dashboard never-counted logic.
 */
export function resolveStockTakeDashboardValidDateFloorMs(nowMs: number = Date.now()): number {
  return nowMs - STOCK_TAKE_DASHBOARD_NEVER_COUNTED_YEARS * 365.25 * 86_400_000;
}

/**
 * True when the stock item has no usable last stock take date.
 */
export function isNeverCountedStockTakeDate(raw: unknown): boolean {
  if (raw == null) {
    return true;
  }
  const ms = parseStockTakeDateToMillis(raw);
  if (ms == null) {
    return true;
  }
  return ms < STOCK_TAKE_NEVER_COUNTED_CUTOFF_MS;
}

/**
 * Days since last stock take, or null when never counted.
 */
export function resolveStockTakeDaysSinceCounted(raw: unknown, nowMs: number = Date.now()): number | null {
  if (isNeverCountedStockTakeDate(raw)) {
    return null;
  }
  const atMs = parseStockTakeDateToMillis(raw);
  if (atMs == null) {
    return null;
  }
  return Math.floor((nowMs - atMs) / 86_400_000);
}

/**
 * Parse a stock take date from a Firestore stock document field.
 */
export function parseStockTakeDateToIso(raw: unknown): string | undefined {
  if (raw == null) {
    return undefined;
  }
  if (raw instanceof Timestamp) {
    return raw.toDate().toISOString();
  }
  if (raw instanceof Date) {
    return Number.isNaN(raw.getTime()) ? undefined : raw.toISOString();
  }
  if (typeof raw === 'object' && raw !== null && 'seconds' in raw) {
    const seconds = (raw as { seconds: number }).seconds;
    const nanoseconds = (raw as { nanoseconds?: number }).nanoseconds ?? 0;
    return Timestamp.fromMillis(seconds * 1000 + Math.floor(nanoseconds / 1_000_000)).toDate().toISOString();
  }
  if (typeof raw === 'number') {
    const ms = raw > 1_000_000_000_000 ? raw : raw * 1000;
    return new Date(ms).toISOString();
  }
  if (typeof raw === 'string' && raw.length > 0) {
    const parsed = Date.parse(raw);
    if (!Number.isNaN(parsed)) {
      return new Date(parsed).toISOString();
    }
  }
  return undefined;
}

/**
 * Parse a stock take date to epoch milliseconds for comparisons.
 */
export function parseStockTakeDateToMillis(raw: unknown): number | null {
  const iso = parseStockTakeDateToIso(raw);
  if (!iso) {
    return null;
  }
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? null : ms;
}
